// @ts-check

class FfFilterMenuDrawer extends HTMLElement {
  /**
   * @type {Record<string, string[]>}
   * Almacena el nombre del parámetro del filtro con un array de los valores que han sido seleccionados
   */
  options_selected = {};
  /**
   * @type {string}
   * Necesita el id de la sección que renderiza los productos para obtenerla actualizada
   * con los resultados de los filtros aplicados
   */
  #section_id;
  /**
   * @type {AbortController | undefined}
   * Controlador de la última petición que se ha hecho para poder abortarla en cuando
   * se hacen varias peticiones al mismo tiempo
   */
  #prev_request_controller;
  /**
   * Selector para la grid de los productos, el contenido de este elemento se reemplazará
   * con cada petición completada con éxito y reemplazará los productos por los obtenidos
   * luego de aplicar los filtros
   */
  #product_grid_id = "product-grid";

  /**
   * Indica si la grid de productos es renderizada por Bloomreach
   */
  bloomreach_enabled = false;

  constructor() {
    super();

    this.bloomreach_enabled = JSON.parse(
      this.dataset.enableBloomreach || "false"
    );

    const sectionId = this.getAttribute("section-id");

    if (!sectionId) throw new Error();

    this.#section_id = sectionId;

    this.registerListenerOpeners();
    this.registerCloseEvent();
    this.registerSortByEvents();
    this.checkOptionsSelection();

    this.querySelector(".ff-drawer-clear-filters")?.addEventListener(
      "click",
      (e) => {
        // @ts-ignore
        if (e.currentTarget.classList.contains("disabled")) return;

        /**
         * @type {NodeListOf<FfFilterOption>}
         */
        const options = this.querySelectorAll("ff-filter-option");
        /**
         * @type {NodeListOf<FfFilterRange>}
         */
        const ranges = this.querySelectorAll("ff-filter-range");

        ranges.forEach((range) => {
          range.reset();
        });

        options.forEach((opt) => {
          if (opt.selected) {
            opt.selected = false;
          }
        });

        this.options_selected = {};

        this.request();
      }
    );

    this.querySelector(".ff-drawer-view-products")?.addEventListener(
      "click",
      (e) => {
        // @ts-ignore
        if (e.currentTarget.classList.contains("loading")) return;

        this.hide();
      }
    );
  }

  /**
   * @param {{name: string, type: string, value: any}[]} facets
   * @param {boolean} firstTime
   */
  renderBloomreachFilters(facets, firstTime) {
    const filtersContainer = this.querySelector(".ff-filters-container");
    const sortByLabel =
      filtersContainer?.querySelector(".ff-filter-sort-by-label") || null;

    if (firstTime) {
      const searchParams = new URLSearchParams(window.location.search);

      Array.from(searchParams.entries()).forEach(([key, value]) => {
        if (key.startsWith("filter.")) {
          this.setOption(key.replace("filter.", ""), value, false, false);
        }
      });
    }

    const sizeLabels = ["Size", "Talla", "Taille", "Taglia", "Grösse"];
    const colorLabels = ["Color", "Couleur", "Colore", "Farbe"];

    facets.forEach((field) => {
      const isSize = sizeLabels.includes(field.name);
      const isColor = colorLabels.includes(field.name);
      const haveShoeSize =
        isSize && !!field.value.find((val) => !isNaN(val.name));

      let clothingSizeLabelDone = false;

      if (firstTime) {
        if (field.type !== "number_stats" && !field.value.length) return;

        const filterContainer = document.createElement("div");

        filterContainer.id = `ff-filter-${field.name}`;

        filterContainer.innerHTML = /* HTML */ `
          <span class="ff-filter-label">${field.name}</span>
          <div class="ff-options-container"></div>
          <div class="ff-divider"></div>
        `;

        filtersContainer?.insertBefore(filterContainer, sortByLabel);

        const optionsContainer = filterContainer.querySelector(
          ".ff-options-container"
        );

        if (field.type === "text" || field.type === "number") {
          if (isSize)
            field.value.sort(({ name: a }, { name: b }) => {
              const aIsShoeSize = !isNaN(a);
              const bIsShoeSize = !isNaN(b);

              if (aIsShoeSize && bIsShoeSize) return parseInt(a) - parseInt(b);
              else if (aIsShoeSize) return -1;
              else return 0;
            });

          if (isSize && optionsContainer && haveShoeSize)
            optionsContainer.innerHTML = `<span class="ff-secondary-label">${this.dataset.shoesSizeLabel}</span>`;

          if (!isColor && !isSize) {
            const filterOption = new FfFilterOption();

            const filterValue = field.value
              .map((value) => value.name)
              .join(";");

            const id = field.name.replace(/( |\/)/g, "-").toLowerCase();

            filterOption.innerHTML = /* HTML */ `
              <input
                target="true"
                type="checkbox"
                id="${id}-all"
                name="${id}-all"
                value="${filterValue}"
              />
              <label for="${id}-all">${this.dataset.seeAllLabel}</label>
            `;

            filterOption.id = `ff-option-${id}-all`;
            filterOption.option_key = field.name;
            filterOption.option_value = filterValue;

            optionsContainer?.appendChild(filterOption);
          }

          field.value.forEach((option) => {
            const filterOption = new FfFilterOption();

            const selected = this.options_selected[
              `filter.${field.name}`
            ]?.includes(option.name);

            if (isSize && !clothingSizeLabelDone && isNaN(option.name)) {
              const span = document.createElement("span");

              span.classList.add("ff-secondary-label");

              span.innerText =
                this.dataset.clothingSizeLabel || "Clothing size";

              optionsContainer?.appendChild(span);

              clothingSizeLabelDone = true;
            }

            if (isSize) {
              filterOption.innerHTML = /* HTML */ `
                <span class="ff-list-option" target="true">
                  ${option.name}
                </span>
              `;
            } else if (isColor) {
              filterOption.innerHTML = /* HTML */ `
                <div target="true" class="ff-list-color">
                  <div
                    class="ff-option-color ${option.name.toLowerCase()}"
                  ></div>
                  <span style="text-transform: capitalize;">
                    ${option.name}
                  </span>
                </div>
              `;
            } else {
              const discountLabels = [
                "Discount",
                "Descuento",
                "Réduction",
                "Sconto",
                "Rabatt",
              ];

              if (
                discountLabels.includes(field.name) &&
                isNaN(parseInt(option.name))
              )
                return;

              const label = (() => {
                if (
                  discountLabels.includes(field.name) &&
                  !isNaN(parseInt(option.name))
                ) {
                  return parseInt(option.name).toString().concat("%");
                } else {
                  return option.name;
                }
              })();

              filterOption.innerHTML = /* HTML */ `
                <input
                  target="true"
                  type="checkbox"
                  id="${option.name}"
                  name="${option.name}"
                  value="${option.name}"
                  ${selected ? "checked" : ""}
                  ${option.count === 0 ? "disabled" : ""}
                />
                <label for="${option.name}" disabled="false">${label}</label>
              `;
            }

            filterOption.id = `ff-option-${option.name}-filter.${field.name}`
              .replace(/( |\/)/g, "-")
              .toLowerCase();
            filterOption.option_key = `${field.name}`;
            filterOption.option_value = option.name;
            filterOption.selected = selected;
            filterOption.disabled = option.count === 0;

            optionsContainer?.appendChild(filterOption);
          });
        } else if (field.type === "number_stats") {
          const filterRange = new FfFilterRange();
          const {
            value: { end },
          } = field;

          filterRange.setAttribute("filter-key", `filter.${field.name}`);
          filterRange.setAttribute("price-gap", "5");
          filterRange.setAttribute("min", "0");
          filterRange.setAttribute("max", end.toString());
          filterRange.setAttribute("step", "5");

          const symbol = window.cartStrings.currency;

          filterRange.innerHTML = /* HTML */ `
            <div class="ff-range-tooltips">
              <div class="ff-range-tooltip" id="ff-min-tooltip">0${symbol}</div>
              <div class="ff-range-tooltip" id="ff-max-tooltip">
                ${end}${symbol}
              </div>
            </div>
            <div class="ff-range-slider">
              <div class="ff-range-slider-progress"></div>
            </div>
            <div class="ff-range-inputs">
              <input type="range" id="ff-range-min" />
              <input type="range" id="ff-range-max" />
            </div>
          `;

          optionsContainer?.appendChild(filterRange);
        } else if (field.type === "number_range") {
          const shippingOptions = field.value
            .map(({ start, end, count }) => {
              if (!count) return;

              const name = (() => {
                if (start === "*") return "24/48h";
                else if (end === "*")
                  return `+${Math.floor(start)} ${this.dataset.weeksLabel}`;
                else
                  return `${
                    Math.ceil(start) === 0 ? "1" : Math.ceil(start)
                  }-${end} ${this.dataset.weeksLabel}`;
              })();

              const value = `[${start} TO ${end}]`;

              return { name, value };
            })
            .filter((opt) => !!opt);

          shippingOptions.forEach((option) => {
            const filterOption = new FfFilterOption();

            const selected = this.options_selected[
              `filter.${field.name}`
            ]?.includes(option.value);

            const label = option.name;

            filterOption.innerHTML = /* HTML */ `
              <input
                target="true"
                type="checkbox"
                id="${option.value}"
                name="${option.value}"
                value="${option.value}"
                ${selected ? "checked" : ""}
                ${option.count === 0 ? "disabled" : ""}
              />
              <label for="${option.value}" disabled="false">${label}</label>
            `;

            filterOption.id = `ff-option-${option.value}-filter.${field.name}`
              .replace(/( |\/)/g, "-")
              .toLowerCase();
            filterOption.option_key = `${field.name}`;
            filterOption.option_value = option.value;
            filterOption.selected = selected;
            filterOption.disabled = option.count === 0;

            optionsContainer?.appendChild(filterOption);
          });
        }
      } else {
        if (field.type === "text" || field.type === "number") {
          field.value.forEach((option) => {
            const filterOption = this.querySelector(
              `[id="ff-option-${option.name}-${field.name}"]`
                .replace(/( |\/)/g, "-")
                .toLowerCase()
            );

            if (filterOption) {
              // @ts-ignore
              filterOption.disabled = option.count === 0;
            }
          });
        }
      }
    });

    firstTime && this.checkOptionsSelection();
  }

  /**
   * Busca todos los elementos del DOM que tengan la clase `ff-filter-menu-drawer-open` y registra el evento `click`
   * para mostrar el drawer
   */
  registerListenerOpeners() {
    document
      .querySelectorAll(".ff-filter-menu-drawer-open")
      .forEach((el) => el.addEventListener("click", () => this.show()));
  }

  /**
   * Registra el evento para cerrar el drawer cuando se le da click al overlay o al botón de cerrar
   */
  registerCloseEvent() {
    const closeButton = this.querySelector(".ff-drawer-close");
    const overlay = this.querySelector(".ff-overlay");

    closeButton?.addEventListener("click", () => this.hide());
    overlay?.addEventListener("click", () => this.hide());
  }

  /**
   * Registra la selección de una nueva opción de filtro para actualizar la grid de productos
   */
  registerSortByEvents() {
    this.querySelectorAll(`input[type="radio"][name="ff-sort-by"]`).forEach(
      (option) => {
        option.addEventListener("click", () => {
          // @ts-ignore
          this.setOption("sort_by", option.value, true);
        });
      }
    );
  }

  /**
   * Esconde el drawer
   */
  hide() {
    this.classList.remove("active");
  }

  /**
   * Muestra el drawer
   */
  show() {
    this.classList.add("active");
  }

  /**
   * @param {string} key El nombre del filtro del cual se quieren obtener sus opciones
   */
  getOptions(key) {
    return this.options_selected[key] || [];
  }

  /**
   * @param {string} key Nombre del parámetro de filtro
   * @param {string | string[]} values Valor o lista de valores para agregar
   * @param {boolean} overwrite Indica si se debe sobrescribir por completo el parámetro de filtro
   * @param {boolean} doRequest Indica si se hacer la petición con los nuevos filtros
   * @description Añade uno o mas options para un filtro
   */
  setOption(key, values, overwrite = false, doRequest = true) {
    if (this.bloomreach_enabled) {
      key = `filter.${key}`;
    }

    if (overwrite) {
      this.options_selected[key] = Array.isArray(values) ? values : [values];
    } else {
      if (!this.options_selected[key]) this.options_selected[key] = [];

      if (Array.isArray(values)) {
        this.options_selected[key].push(
          ...values.filter((val) => !this.options_selected[key].includes(val))
        );
      } else if (!this.options_selected[key].includes(values))
        this.options_selected[key].push(values);
    }

    if (doRequest) {
      this.request();
    }
  }

  /**
   * @param {{key: string, values: string[]}[]} options Opciones de filtro que se quieren aplicar
   * @param {boolean} overwrite Si está a `true` sobrescribe todos los valores para la clave del filtro
   * @param {boolean} doRequest Si está a `true` realizará la actualización de la grid de productos
   * @description Utilízalo para establecer valores de filtros diferentes sin hacer peticiones por separado
   */
  setMultipleOptions(options, overwrite = false, doRequest = true) {
    options.forEach(({ key, values }) => {
      if (overwrite) {
        this.options_selected[key] = values;
      } else {
        if (!this.options_selected[key]) this.options_selected[key] = values;
        else
          this.options_selected[key].push(
            ...values.filter((val) => !this.options_selected[key].includes(val))
          );
      }
    });

    if (doRequest) {
      this.request();
    } else {
      const filterOptions = Object.values(this.options_selected);

      const filterApplied = !!filterOptions.find((opts) => opts.length);

      if (!filterApplied) {
        this.querySelector(".ff-drawer-clear-filters")?.classList.add(
          "disabled"
        );
      } else {
        this.querySelector(".ff-drawer-clear-filters")?.classList.remove(
          "disabled"
        );
      }
    }
  }

  /**
   * @param {string} key Nombre del parámetro de filtro
   * @param {string | string[]} values Valor o lista de valores para eliminar
   * @description Elimina uno o mas options para un filtro
   */
  removeOption(key, values) {
    if (this.bloomreach_enabled) {
      key = `filter.${key}`;
    }

    if (!this.options_selected[key] || !this.options_selected[key].length)
      return;

    const toExclude = Array.isArray(values) ? values : [values];

    this.options_selected[key] = this.options_selected[key].filter(
      (opt) => !toExclude.includes(opt)
    );

    this.request();
  }

  /**
   * @param {string} key Nombre del parámetro de filtro
   * @description Elimina todas las opciones seleccionadas para un filtro especifico
   */
  removeAll(key) {
    this.options_selected[key] = [];

    this.request();
  }

  /**
   * Inicializa el estado de las opciones de los filtros seleccionados
   */
  checkOptionsSelection() {
    /**
     * @type {NodeListOf<FfFilterOption>}
     */
    const options = this.querySelectorAll("ff-filter-option");

    options.forEach((opt) => {
      if (opt.id.includes("-all")) return;

      if (opt.getAttribute("selected") === "true") {
        const key = opt.getAttribute("option-key");
        const value = opt.getAttribute("option-value");

        if (!key || !value) return;

        if (!this.options_selected[key]) this.options_selected[key] = [value];
        else this.options_selected[key].push(value);
      }
    });

    const noFilters = !Object.entries(this.options_selected).length;

    if (noFilters) {
      this.querySelector(".ff-drawer-clear-filters")?.classList.add("disabled");
    } else {
      this.querySelector(".ff-drawer-clear-filters")?.classList.remove(
        "disabled"
      );
    }
  }

  /**
   * Obtiene la section de la grid de productos actualizada con los resultados de los filtros
   * y reemplaza los productos en la grid que está vendo el cliente
   */
  async request() {
    const options = Object.values(this.options_selected);

    const filterApplied = !!options.find((opts) => opts.length);

    if (!filterApplied) {
      this.querySelector(".ff-drawer-clear-filters")?.classList.add("disabled");
    } else {
      this.querySelector(".ff-drawer-clear-filters")?.classList.remove(
        "disabled"
      );
    }

    const collectionContainer = document.querySelector(
      "#ProductGridContainer .collection"
    );

    const clearButton = this.querySelector(".ff-drawer-clear-filters");
    const viewButton = this.querySelector(".ff-drawer-view-products");

    viewButton?.classList.add("loading");
    clearButton?.classList.add("loading");
    collectionContainer?.classList.add("loading");

    // Si hay una petición pendiente la abortamos
    if (this.#prev_request_controller) {
      this.#prev_request_controller.abort();
    }

    const controller = new AbortController();

    // Almacenamos el controlador de la petición para que pueda ser abortada en caso de ser necesario
    this.#prev_request_controller = controller;

    // Iteramos los filtros aplicados y generamos los parámetros de url para la consulta
    const filterParams = Object.entries(this.options_selected).reduce(
      (prev, [key, values]) => {
        const filters = values
          .map(
            (value) =>
              `${
                this.bloomreach_enabled &&
                value &&
                !key.toLowerCase().includes("filter.")
                  ? "filter."
                  : ""
              }${key}=${value}`
          )
          .join("&");

        if (filters) prev += `&${filters}`;

        return prev;
      },
      ""
    );

    const params = new URLSearchParams(filterParams);

    if (this.bloomreach_enabled) {
      history.pushState(
        null,
        "",
        `${window.location.pathname}?${params.toString()}`
      );

      // @ts-ignore
      window.__br__.methods.renderPLP(true);

      collectionContainer?.classList.remove("loading");
    } else {
      try {
        const res = await fetch(
          `${window.location.pathname}?section_id=${
            this.#section_id
          }&${params.toString()}`,
          { signal: controller.signal }
        );

        const result = await res.text();

        const sectionDom = new DOMParser().parseFromString(result, "text/html");

        const newGrid = sectionDom.getElementById(this.#product_grid_id);
        const prevGrid = document.getElementById(this.#product_grid_id);

        const newPagination = sectionDom.getElementById("AjaxinatePagination");
        const prevPagination = document.getElementById("AjaxinatePagination");

        /**
         * @type {NodeListOf<FfFilterOption>}
         */
        const currentOptions = this.querySelectorAll("ff-filter-option");
        /**
         * @type {NodeListOf<FfFilterOption>}
         */
        const updatedOptions = sectionDom.querySelectorAll("ff-filter-option");

        let totalProducts = 0;

        /**
         * Iteramos las opciones de los filtros luego de aplicar los nuevos valores
         * para actualizar su estado, si están o no están disponibles con los nuevos resultados
         */
        currentOptions.forEach((opt) => {
          const sameOpt = Array.from(updatedOptions).find(
            (uOpt) => uOpt.id === opt.id
          );

          if (sameOpt) {
            const selected = JSON.parse(
              sameOpt.getAttribute("selected") || "false"
            );
            const disabled = JSON.parse(
              sameOpt.getAttribute("disabled") || "false"
            );

            opt.disabled = disabled;
            opt.selected = selected;

            if (opt.selected && opt.products_count > totalProducts) {
              totalProducts = opt.products_count;
            }
          }
        });

        if (newPagination && prevPagination) {
          prevPagination.replaceWith(newPagination);
        }

        if (newGrid && prevGrid) {
          prevGrid.replaceWith(newGrid);

          history.pushState(
            null,
            "",
            `${window.location.pathname}?${params.toString()}`
          );

          /**
           * TODO = ¿Hace falta eliminar la instancia anterior de ajaxinate?
           */
          // @ts-ignore
          new Ajaxinate({
            container: "#product-grid",
            pagination: "#AjaxinatePagination",
            method: "scroll",
            offset: 2000,
            loadingText:
              '<div class="loading_spinner__overlay"> <div class="bottom--popup loading--spinner"> <div class="loading--animation-container"> <div class="loading--bar"></div> </div> </div> </div>',
          });

          collectionContainer?.classList.remove("loading");
        }
      } catch (error) {
        if (error.name !== "AbortError") {
          console.log(error);

          collectionContainer?.classList.remove("loading");
        }
      }
    }

    viewButton?.classList.remove("loading");
    clearButton?.classList.remove("loading");
  }
}

class FfFilterOption extends HTMLElement {
  /**
   * @type {string}
   * Nombre del parámetro del filtro
   */
  option_key;
  /**
   * @type {string}
   * Valor de la opción del filtro
   */
  option_value;

  /**
   * @type {FfFilterMenuDrawer}
   * El drawer que contiene las opciones de los filtros
   */
  #filter_drawer;

  /**
   * @type {FfFilterOption | null}
   * Elemento que selecciona/deselecciona todas las opciones del mismo filtro
   */
  #all_el;

  /**
   * Elemento al cual se va a registrar el evento para aplicar o quitar el filtro
   */
  #target_el;

  /**
   * Cantidad de productos que se muestran con este filtro aplicado
   */
  products_count;

  /**
   * @param {boolean} value
   * Nunca modifiques directamente el atributo `disabled`, para definir su valor solo debes
   * `filterOption.disabled = true`
   */
  set disabled(value) {
    this.setAttribute("disabled", `${value || false}`);

    if (this.#target_el?.nodeName === "INPUT") {
      if (value) {
        // @ts-ignore
        this.#target_el.setAttribute("disabled", "disabled");
      } else {
        // @ts-ignore
        this.#target_el.removeAttribute("disabled");
      }
    }
  }

  /**
   * Devuelve el valor actual del atributo `disabled`, su valor será `true` si no hay resultados
   * cuando se selecciona está opción del filtro
   */
  get disabled() {
    return JSON.parse(this.getAttribute("disabled") || "false");
  }

  /**
   * @param {boolean} value
   * Nunca modifiques directamente el atributo `selected`, para definir su valor solo debes
   * `filterOption.selected = true`
   */
  set selected(value) {
    this.setAttribute("selected", `${value || false}`);

    if (this.#target_el?.nodeName === "INPUT") {
      // @ts-ignore
      this.#target_el.checked = value;
    }
  }

  /**
   * Devuelve el valor actual del atributo `selected`, indica si la opción está aplicada en los filtros
   */
  get selected() {
    return JSON.parse(this.getAttribute("selected") || "false");
  }

  constructor() {
    super();
  }

  connectedCallback() {
    !this.option_key &&
      (this.option_key = this.getAttribute("option-key") || "");
    !this.option_value &&
      (this.option_value = this.getAttribute("option-value") || "");
    !this.products_count &&
      (this.products_count = parseInt(
        this.getAttribute("products-count") || "0"
      ));

    /**
     * @type {FfFilterMenuDrawer | null}
     */
    const drawer = document.querySelector("ff-filter-menu-drawer");

    if (!drawer) throw new Error("No parent drawer was found");

    this.#filter_drawer = drawer;

    /**
     * @type {FfFilterOption | null}
     */
    const allEl = this.#filter_drawer.querySelector(
      `ff-filter-option[id="ff-option-${this.option_key
        .replace(/( |\/)/g, "-")
        .toLowerCase()}-all"]`
    );

    this.#all_el = allEl;

    this.registerTargetEl();
    this.listenSelection();
  }

  registerTargetEl() {
    const elementTarget = Array.from(this.childNodes).find((node) => {
      if (node.nodeType === 1) {
        // @ts-ignore
        const isTarget = node.getAttribute("target");

        return isTarget;
      }
    });

    if (!elementTarget) console.error("No target element was defined");

    // @ts-ignore
    this.#target_el = elementTarget;
  }

  /**
   * Registra el evento que corresponde para aplicar el filtro de la opción al elemento target
   */
  listenSelection() {
    if (this.#target_el?.nodeName === "INPUT") {
      this.#target_el.addEventListener("input", () => {
        if (this.disabled) return;

        this.selected = !this.selected;

        let options = this.option_value.split(";");

        if (this.id.includes("-all")) {
          options = options.filter((opt) => {
            /**
             * @type {FfFilterOption | null}
             */
            const optionEl = this.#filter_drawer.querySelector(
              `ff-filter-option[id="ff-option-${opt}-${
                this.#filter_drawer.bloomreach_enabled ? "filter." : ""
              }${this.option_key}"]`
                .replace(/( |\/)/g, "-")
                .toLowerCase()
            );

            if (optionEl) {
              const disabled = optionEl.disabled;

              if (!disabled) {
                optionEl.selected = this.selected;

                return true;
              }
            }
          });
        }

        //@ts-ignore
        if (!this.selected)
          this.#filter_drawer.removeOption(this.option_key, options);
        else this.#filter_drawer.setOption(this.option_key, options);

        if (!this.id.includes("-all") && this.#all_el) {
          const currentOptions = this.#filter_drawer.getOptions(
            this.option_key
          );

          this.#all_el.selected =
            currentOptions.length ===
            this.#all_el.option_value.split(";").length;
        }
      });
    } else {
      this.#target_el?.addEventListener("click", () => {
        if (this.disabled) return;

        this.selected = !this.selected;

        if (!this.selected)
          this.#filter_drawer.removeOption(this.option_key, this.option_value);
        else this.#filter_drawer.setOption(this.option_key, this.option_value);
      });
    }
  }
}

class FfFilterRange extends HTMLElement {
  /**
   * @type {number}
   * Distancia mínima que puede haber entre el precio mínimo y el máximo
   */
  #price_gap = parseFloat(this.getAttribute("price-gap") || "0");
  /**
   * @type {number}
   * Valor mas bajo permitido del rango
   */
  #min = parseFloat(this.getAttribute("min") || "0");
  /**
   * @type {number}
   * Valor mas alto permitido del rango
   */
  #max = parseFloat(this.getAttribute("max") || "0");
  /**
   * @type {number}
   * Cual es el valor de cambio para el rango
   */
  #step = parseFloat(this.getAttribute("step") || "0");
  /**
   * @type {string}
   * Nombre del parámetro de filtro
   */
  #filter_key = this.getAttribute("filter-key") || "";
  /**
   * @type {FfFilterMenuDrawer}
   * Drawer que contiene las opciones de filtro
   */
  #filter_drawer;
  /**
   * @type {HTMLInputElement}
   * Input que contiene el precio mínimo del rango
   */
  #min_input;
  /**
   * @type {HTMLInputElement}
   * Input que contiene el precio máximo del rango
   */
  #max_input;
  /**
   * @type {HTMLDivElement}
   * Elemento para mostrar visualmente el rango seleccionado
   */
  #progress_bar;
  /**
   * @type {HTMLDivElement}
   * Label del valor mínimo
   */
  #min_label;
  /**
   * @type {HTMLDivElement}
   * Label para el valor máximo
   */
  #max_label;
  /**
   * @type {number | undefined}
   * Id del último timeout registrado, para poder ser cancelado si es abortado
   */
  #last_timeout;

  /**
   * Devuelve los valores de progreso para los rangos, util para crear elementos visuales
   */
  get progress() {
    const minVal = parseInt(this.#min_input.value);
    const maxVal = parseInt(this.#max_input.value);

    const range = this.#max - this.#min;
    const startRange = minVal - this.#min;
    const endRange = range - (this.#max - maxVal);

    const minPercent = (startRange / range) * 100;
    const maxPercent = 100 - (endRange / range) * 100;

    return { minPercent, maxPercent };
  }

  /**
   * @param {{minPercent: number, maxPercent: number}} progress
   * Modifica el elemento de la barra de progreso con los porcentajes del rango
   */
  set progress({ minPercent, maxPercent }) {
    this.#progress_bar.style.left = `${minPercent}%`;
    this.#progress_bar.style.right = `${maxPercent}%`;

    const drawer = this.#filter_drawer.querySelector(".ff-drawer");

    const drawerRects = drawer?.getBoundingClientRect();
    const progressRects = this.#progress_bar.getBoundingClientRect();
    const minLabelRects = this.#min_label.getBoundingClientRect();
    const maxLabelRects = this.#max_label.getBoundingClientRect();

    if (drawerRects && progressRects && minLabelRects && maxLabelRects) {
      const left =
        progressRects.left - drawerRects.left - minLabelRects.width / 2;
      const right =
        drawerRects.right - progressRects.right - maxLabelRects.width / 2;

      this.#min_label.style.left = `${left}px`;
      this.#max_label.style.right = `${right}px`;
    }
  }

  constructor() {
    super();
  }

  connectedCallback() {
    /**
     * @type {FfFilterMenuDrawer | null}
     */
    const drawer = this.closest("ff-filter-menu-drawer");
    /**
     * @type {HTMLInputElement | null}
     */
    const minInput = this.querySelector("#ff-range-min");
    /**
     * @type {HTMLInputElement | null}
     */
    const maxInput = this.querySelector("#ff-range-max");
    /**
     * @type {HTMLDivElement | null}
     */
    const progressBar = this.querySelector(".ff-range-slider-progress");
    /**
     * @type {HTMLDivElement | null}
     */
    const minLabel = this.querySelector("#ff-min-tooltip");
    /**
     * @type {HTMLDivElement | null}
     */
    const maxLabel = this.querySelector("#ff-max-tooltip");

    if (
      !drawer ||
      !minInput ||
      !maxInput ||
      !progressBar ||
      !minLabel ||
      !maxLabel
    )
      throw new Error();

    this.#filter_drawer = drawer;
    this.#min_input = minInput;
    this.#max_input = maxInput;
    this.#progress_bar = progressBar;
    this.#min_label = minLabel;
    this.#max_label = maxLabel;

    this.#price_gap = parseFloat(this.getAttribute("price-gap") || "0");
    this.#min = parseFloat(this.getAttribute("min") || "0");
    this.#max = parseFloat(this.getAttribute("max") || "0");
    this.#step = parseFloat(this.getAttribute("step") || "0");
    this.#filter_key = this.getAttribute("filter-key") || "";

    [this.#min_input, this.#max_input].forEach((input) => {
      input.max = this.#max.toString();
      input.min = this.#min.toString();
      input.step = this.#step.toString();
    });

    setTimeout(() => {
      const searchParams = new URLSearchParams(window.location.search);

      const minDefined = searchParams.get(`${this.#filter_key}.gte`);
      const maxDefined = searchParams.get(`${this.#filter_key}.lte`);

      const minDifferent = minDefined && minDefined !== this.#min.toString();
      const maxDifferent = maxDefined && maxDefined !== this.#max.toString();

      this.#min_input.value = minDefined ? minDefined : this.#min.toString();
      this.#max_input.value = maxDefined ? maxDefined : this.#max.toString();

      this.#min_label.innerText = `${this.#min_input.value}${
        // @ts-ignore
        window.cartStrings.currency
      }`;
      this.#max_label.innerText = `${this.#max_input.value}${
        // @ts-ignore
        window.cartStrings.currency
      }`;

      this.updateProgress();
      this.listenRangeChange();

      minDifferent &&
        maxDifferent &&
        this.#filter_drawer.setMultipleOptions(
          [
            { key: `${this.#filter_key}.gte`, values: [this.#min_input.value] },
            { key: `${this.#filter_key}.lte`, values: [this.#max_input.value] },
          ],
          true,
          false
        );
    }, 1000);
  }

  /**
   * Registramos el evento que escucha los cambios en los inputs del rango de precio
   */
  listenRangeChange() {
    [this.#min_input, this.#max_input].forEach((input) => {
      input.addEventListener("input", (e) => {
        const minVal = parseInt(this.#min_input.value);
        const maxVal = parseInt(this.#max_input.value);

        // Prevenimos que los valores de los rangos sean incorrectos
        if (maxVal - minVal < this.#price_gap) {
          if (input.id === "ff-range-min") {
            this.#min_input.value = (maxVal - this.#price_gap).toString();
          } else {
            this.#max_input.value = (minVal + this.#price_gap).toString();
          }
        } else {
          // @ts-ignore
          this.#min_label.innerText = `${minVal}${window.cartStrings.currency}`;
          // @ts-ignore
          this.#max_label.innerText = `${maxVal}${window.cartStrings.currency}`;

          this.updateProgress();
          this.prepareForUpdate();
        }
      });
    });
  }

  /**
   * Registramos un timeout para dar tiempo a que el usuario defina su selección y luego
   * actualizamos las opciones de filtro para obtener los nuevos resultados
   */
  prepareForUpdate() {
    if (this.#last_timeout) clearInterval(this.#last_timeout);

    this.#last_timeout = setTimeout(() => {
      this.#filter_drawer.setMultipleOptions(
        [
          { key: `${this.#filter_key}.gte`, values: [this.#min_input.value] },
          { key: `${this.#filter_key}.lte`, values: [this.#max_input.value] },
        ],
        true
      );
    }, 500);
  }

  reset() {
    this.#min_input.value = this.#min.toString();
    this.#max_input.value = this.#max.toString();

    this.#min_label.innerText = `${this.#min_input.value}${
      // @ts-ignore
      window.cartStrings.currency
    }`;
    this.#max_label.innerText = `${this.#max_input.value}${
      // @ts-ignore
      window.cartStrings.currency
    }`;

    this.updateProgress();
  }

  /**
   * Actualiza los componentes visuales que muestran el progreso de los rangos
   */
  updateProgress() {
    this.progress = this.progress;
  }
}

customElements.define("ff-filter-menu-drawer", FfFilterMenuDrawer);
customElements.define("ff-filter-option", FfFilterOption);
customElements.define("ff-filter-range", FfFilterRange);
